// ─── Promoter Tracking Utility ──────────────────────────────────────────────
// Detects ?ref=CODE on any page URL, stores it in localStorage (+ cookie if
// consent given) for a 7-day attribution window, and records a click in the
// promoter_link_clicks table.
//
// Usage: call initPromoterTracking() once on app load (in App.js useEffect).
// ────────────────────────────────────────────────────────────────────────────

import { supabase } from "../supabaseClient";
import { setTrackingCookie, clearTrackingCookie, getVisitorId } from "./trackingCore";
import logger from "./logger";

const PROMOTER_REF_KEY = "promoter_ref";
const PROMOTER_REF_TS_KEY = "promoter_ref_timestamp";
const ATTRIBUTION_WINDOW_DAYS = 7;

/**
 * Read the stored promoter referral code (if still within the 7-day window).
 * @returns {string|null}
 */
export const getStoredPromoterRef = () => {
  const code = localStorage.getItem(PROMOTER_REF_KEY);
  const ts = localStorage.getItem(PROMOTER_REF_TS_KEY);
  if (!code || !ts) return null;

  const daysSince = (Date.now() - parseInt(ts, 10)) / (1000 * 60 * 60 * 24);
  if (daysSince > ATTRIBUTION_WINDOW_DAYS) {
    // Expired — clean up
    clearPromoterRef();
    return null;
  }
  return code;
};

/**
 * Clear the stored promoter ref (called after successful attribution or expiry).
 */
export const clearPromoterRef = () => {
  localStorage.removeItem(PROMOTER_REF_KEY);
  localStorage.removeItem(PROMOTER_REF_TS_KEY);
  clearTrackingCookie(PROMOTER_REF_KEY);
};

/**
 * Store the promoter referral code with a timestamp.
 * @param {string} code
 */
const storePromoterRef = (code) => {
  const now = Date.now().toString();
  localStorage.setItem(PROMOTER_REF_KEY, code);
  localStorage.setItem(PROMOTER_REF_TS_KEY, now);

  const maxAge = ATTRIBUTION_WINDOW_DAYS * 24 * 60 * 60;
  setTrackingCookie(PROMOTER_REF_KEY, code, maxAge);
};

/**
 * Record a click in the promoter_link_clicks table.
 * @param {number} promoterId
 * @param {string} pagePath
 */
const trackPromoterClick = async (promoterId, pagePath) => {
  try {
    const visitorId = getVisitorId();

    await supabase.from("promoter_link_clicks").insert({
      promoter_id: promoterId,
      visitor_id: visitorId,
      page_path: pagePath,
      referrer: document.referrer || null,
      user_agent: navigator.userAgent || null,
    });
  } catch (err) {
    logger.error("[PromoterTracking] Failed to record click", err);
  }
};

/**
 * Initialise promoter tracking. Should be called once on app load.
 *
 * 1. Reads ?ref=CODE from the current URL
 * 2. Validates the code exists in the promoters table
 * 3. Stores it in localStorage/cookie
 * 4. Records a click
 * 5. Removes the ?ref= param from the URL (clean up — no page reload)
 * 6. If dispatch provided, sets promoterRef in Redux for public access
 *
 * @param {Function} [dispatch] - Redux dispatch function (optional, enables public access)
 */
export const initPromoterTracking = async (dispatch) => {
  try {
    const url = new URL(window.location.href);
    const refCode = url.searchParams.get("ref");

    // Even without a URL param, restore promoterRef from localStorage for returning visitors
    if (!refCode) {
      if (dispatch) {
        const storedRef = getStoredPromoterRef();
        if (storedRef) {
          const { setPromoterRef } = await import("../slices/userReducer");
          dispatch(setPromoterRef(storedRef));
        }
      }
      return;
    }

    // Look up the promoter by referral code
    const { data: promoter, error } = await supabase
      .from("promoters")
      .select("id, referral_code, status")
      .eq("referral_code", refCode)
      .eq("status", "active")
      .maybeSingle();

    if (error || !promoter) {
      // Invalid code — ignore silently
      return;
    }

    // Store the ref code for attribution at signup time
    storePromoterRef(promoter.referral_code);

    // Set promoterRef in Redux so the auth guard grants public access
    if (dispatch) {
      const { setPromoterRef } = await import("../slices/userReducer");
      dispatch(setPromoterRef(promoter.referral_code));
    }

    // Record the click
    await trackPromoterClick(promoter.id, url.pathname);

    // Clean up the URL (remove ?ref= so it doesn't stick around / get shared again)
    url.searchParams.delete("ref");
    window.history.replaceState({}, "", url.pathname + url.search + url.hash);
  } catch (err) {
    logger.error("[PromoterTracking] init error", err);
  }
};
